import {
  COUNTRIES,
  CrmType,
  CrmTypeName,
  FNIType,
  Origin,
  SourceType,
  TradeInType,
} from '@upstart/harmony/lib/constants';
import { Dealer } from '@upstart/harmony/lib/legos/dealerTypes';

export const getStep4Schema = (dealer: Dealer) => {
  const { country } = dealer;
  const getTradeInTypeValues = Object.values(TradeInType).filter(
    (type) =>
      country !== COUNTRIES.CA ||
      (country === COUNTRIES.CA && type !== TradeInType.FIRSTLOOK),
  );
  const crmConfig = {
    type: 'object',
    title: 'CRM Settings',
    properties: {
      skateAlerts: {
        type: 'boolean',
        title: 'Enable Skate Alerts',
      },
      restrictedDealAccess: {
        type: 'boolean',
        title: 'Restrict Deal Access',
      },
      // VinSolutions only
      editSoldUndelivered: {
        type: 'boolean',
        title: 'Allow Edits to Sold Pending Finance & Sold On Order.',
      },
      useAppointmentsIntegration: {
        type: 'boolean',
        title: 'Use the Appointments Integration feature',
      },
    },
  };
  const crmDefaults = {
    type: 'object',
    title: '',
    properties: {
      type: {
        type: 'string',
        title: 'Default Source Type',
        default: SourceType.INTERNET,
      },
      sourceId: { type: ['string', 'null'], title: 'Default Source ID' },
      crmUserId: {
        type: ['string', 'null'],
        title:
          'Internet Source CRM User ID (Used in all CRMs, but absolutely required if using Online in Reynolds)',
      },
      adminUserId: { type: ['string', 'null'], title: 'Admin User ID' },
      automationUserId: {
        type: ['string', 'null'],
        title: 'Automation Tests User ID',
      },
      subscriptionId: {
        type: ['string', 'null'],
        title: 'Fortellis Subscription ID',
      },
    },
  };

  const required = [];
  if (dealer.crmType === CrmType.STANDALONE) {
    required.push('otherCrmName');
  }

  return {
    title: 'Integrations',
    type: 'object',
    required,
    properties: {
      crmType: {
        type: 'string',
        title: 'CRM Type',
        anyOf: [
          {
            type: 'string',
            title: 'Elead',
            enum: [CrmType.ELEAD],
          },
          {
            type: 'string',
            title: 'DealerSocket',
            enum: [CrmType.DEALERSOCKET],
          },
          {
            type: 'string',
            title: 'Reynolds and Reynolds',
            enum: [CrmType.REYNOLDS],
          },
          {
            type: 'string',
            title: 'VinSolutions',
            enum: [CrmType.VINSOLUTIONS],
          },
          {
            type: 'string',
            title: 'HigherGear',
            enum: [CrmType.HIGHERGEAR],
          },
          {
            type: 'string',
            title: CrmTypeName.dgdgsalesforce,
            enum: [CrmType.DGDG_SALESFORCE],
          },
          {
            type: 'string',
            title: CrmTypeName.tekion,
            enum: [CrmType.TEKION],
          },
          {
            type: 'string',
            title: CrmTypeName.drivecentric,
            enum: [CrmType.DRIVECENTRIC],
          },
          {
            type: 'string',
            title: 'Other',
            enum: [CrmType.STANDALONE],
          },
        ],
      },
      otherCrmName: {
        type: 'string',
        title: 'Other CRM Name',
      },
      dealersocket: {
        type: 'object',
        title: '',
        properties: {
          dealersocketId: { type: 'string', title: 'Dealersocket ID' },
          dealersocketSiteNumber: {
            type: 'string',
            title: 'Dealersocket Site Number',
          },
        },
      },
      reynolds: {
        type: 'object',
        title: '',
        required: ['defaultStatusId'],
        properties: {
          defaultStatusId: {
            type: 'string',
            title: 'Reynolds Default Status ID',
          },
          demoCounterText: {
            type: 'string',
            title: 'Reynolds Demo Counter Text',
          },
        },
      },
      vinsolutions: {
        type: 'object',
        title: '',
        properties: {
          vinsolutionsId: { type: 'string', title: 'Vin Solutions ID' },
        },
      },
      elead: {
        type: 'object',
        title: '',
        properties: {
          appointmentDropdownId: {
            type: 'string',
            title:
              'Appointment ID - required for enabling appointment integration. (When creating a new appointment, this is the ID of the option in the dropdown)',
          },
        },
      },
      highergear: {
        type: 'object',
        title: '',
        properties: {
          higherGearUrl: { type: 'string', title: 'Highergear URL' },
        },
      },
      tekion: {
        type: 'object',
        title: '',
        properties: {
          dealerId: { type: 'string', title: 'Tekion dealer Id' },
        },
      },
      drivecentricId: {
        type: 'string',
        title: 'DriveCentric Store ID',
      },
      crmEmail: {
        type: 'string',
        title:
          'ADF Email Address for All Cars (will be deprecated soon by the two fields below)',
      },
      crmEmailNew: { type: 'string', title: 'ADF Email Address for New Cars' },
      crmEmailUsed: {
        type: 'string',
        title: 'ADF Email Address for Used Cars',
      },
      crmDefaults,
      crmConfig,
      fniType: {
        type: 'string',
        title: 'F&I Type',
        enum: Object.values(FNIType),
      },
      routeone: {
        type: 'object',
        title: '',
        properties: {
          routeOneId: { type: 'string', title: 'RouteOne ID' },
          routeOneFallbackUserId: {
            type: ['string', 'null'],
            title: 'Default RouteOne User ID',
          },
          enableSuppressR1Application: {
            type: 'boolean',
            title: 'Suppress duplicate R1 credit application',
          },
        },
      },
      dealertrack: {
        type: 'object',
        title: '',
        required: ['dealertrackId'],
        properties: {
          dealertrackId: { type: 'string', title: 'DealerTrack ID' },
        },
      },
      cudl: {
        type: 'object',
        title: '',
        properties: {
          cudlId: { type: 'string', title: 'CUDL ID' },
        },
      },
      tradeInManagerType: {
        type: 'string',
        title: 'TradeIn Type',
        enum: getTradeInTypeValues,
      },
      vauto: {
        type: 'object',
        title: 'VAuto',
        properties: {
          enabled: {
            type: 'object',
            title: 'Enabled',
            properties: {
              [Origin.ONLINE]: {
                type: 'boolean',
                title: 'Online',
                default: false,
              },
              [Origin.INSTORE]: {
                type: 'boolean',
                title: 'In-store',
                default: false,
              },
            },
          },
          entityId: { type: 'string', title: 'API Entity ID' },
          username: { type: 'string', title: 'API Username' },
          login: {
            type: 'object',
            title: 'Login',
            properties: {
              username: { type: 'string', title: 'Username' },
              password: { type: 'string', title: 'Password' },
            },
          },
        },
      },
      firstlook: {
        type: 'object',
        title: 'FirstLook',
        properties: {
          dealerId: { type: 'string', title: 'Dealer ID' },
          partnerId: { type: 'number', title: 'Partner ID' },
          passcode: { type: 'string', title: 'Passcode' },
        },
      },
      featureConfig: {
        type: 'object',
        title: 'OEM Integrations',
        properties: {
          shiftDigital: {
            type: 'object',
            title: 'Shift Digital',
            description: 'Configure Shift Digital Integration',
            properties: {
              enabled: {
                title: 'Enabled',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
              taggingEnabled: {
                title: 'Enable Shift Event Tagging',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
            },
            dependencies: {
              enabled: {
                oneOf: [
                  {
                    properties: {
                      enabled: {
                        enum: [false],
                      },
                    },
                  },
                  {
                    required: ['retailerID', 'apiToken'],
                    properties: {
                      enabled: {
                        enum: [true],
                      },
                      retailerID: {
                        title: 'Retailer ID',
                        type: 'string',
                      },
                      dealerID: {
                        title: 'Dealer ID',
                        type: 'string',
                      },
                      apiToken: {
                        title: 'API Token',
                        type: 'string',
                      },
                      useShiftOffers: {
                        title: 'Use Shift Offers',
                        type: 'boolean',
                      },
                      importShiftAccessories: {
                        title: 'Import Shift Accessories',
                        type: 'boolean',
                        default: true,
                      },
                      importShiftPricing: {
                        title: 'Import Shift Pricing',
                        type: 'boolean',
                        default: true,
                      },
                    },
                  },
                ],
              },
              taggingEnabled: {
                oneOf: [
                  {
                    properties: {
                      taggingEnabled: {
                        enum: [false],
                      },
                    },
                  },
                  {
                    required: ['retailerID', 'apiToken'],
                    properties: {
                      taggingEnabled: {
                        enum: [true],
                      },
                      retailerID: {
                        title: 'Retailer ID',
                        type: 'string',
                      },
                      dealerID: {
                        title: 'Dealer ID',
                        type: 'string',
                      },
                      apiToken: {
                        title: 'API Token',
                        type: 'string',
                      },
                      useShiftOffers: {
                        title: 'Use Shift Offers',
                        type: 'boolean',
                      },
                      importShiftAccessories: {
                        title: 'Import Shift Accessories',
                        type: 'boolean',
                        default: true,
                      },
                      importShiftPricing: {
                        title: 'Import Shift Pricing',
                        type: 'boolean',
                        default: true,
                      },
                    },
                  },
                ],
              },
            },
          },
          unite: {
            type: 'object',
            title: 'Unite',
            description: 'Configure Unite Integration',
            properties: {
              enabled: {
                title: 'Enabled',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
              taggingEnabled: {
                title: 'Enable Unite Event Tagging',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
            },
            dependencies: {
              enabled: {
                oneOf: [
                  {
                    properties: {
                      enabled: {
                        enum: [false],
                      },
                    },
                  },
                  {
                    required: ['dealerCode'],
                    properties: {
                      enabled: {
                        enum: [true],
                      },
                      dealerCode: {
                        title: 'Dealer Code',
                        type: 'string',
                      },
                    },
                  },
                ],
              },
              taggingEnabled: {
                oneOf: [
                  {
                    properties: {
                      taggingEnabled: {
                        enum: [false],
                      },
                    },
                  },
                  {
                    required: ['dealerCode'],
                    properties: {
                      taggingEnabled: {
                        enum: [true],
                      },
                      dealerCode: {
                        title: 'Dealer Code',
                        type: 'string',
                      },
                    },
                  },
                ],
              },
            },
          },
          autodata: {
            type: 'object',
            title: 'Autodata',
            description: 'Configure Autodata Integration',
            properties: {
              enabled: {
                title: 'Enabled',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
            },
            dependencies: {
              enabled: {
                oneOf: [
                  {
                    properties: {
                      enabled: {
                        enum: [false],
                      },
                    },
                  },
                  {
                    required: ['dealerId'],
                    properties: {
                      enabled: {
                        enum: [true],
                      },
                      dealerId: {
                        title: 'Dealer ID',
                        type: 'string',
                      },
                    },
                  },
                ],
              },
            },
          },
          informativ: {
            type: 'object',
            title: 'Informativ',
            description: 'Configure Informativ Credit App Integration',
            properties: {
              enabled: {
                title: 'Enabled',
                type: 'boolean',
                enum: [true, false],
                default: false,
              },
            },
            dependencies: {
              enabled: {
                oneOf: [
                  {
                    properties: {
                      enabled: {
                        enum: [false],
                      },
                    },
                  },
                  {
                    required: ['dealershipId'],
                    properties: {
                      enabled: {
                        enum: [true],
                      },
                      dealershipId: {
                        title: 'Informativ Dealership ID',
                        type: 'string',
                      },
                    },
                  },
                ],
              },
            },
          },
        },
      },
    },
  };
};
